/**
 * Claude Code hand-offs (moved from CEO Tasks): hand a task to one of your Claude Code sessions — a project folder on
 * your Mac. The helper there works on it and posts the report back onto the task. Only shown to people it is set up for.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, CheckCircle2, CircleAlert, Loader2, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, post, put } from "../../lib/api";
import { Card, ErrorNote, fmtDate } from "../ui";

interface Session { id: string; name: string; path: string }
interface Helper { connected: boolean; seenAt: string | null; configured: boolean }
interface Handoff { id: string; name: string; note: string; status: "pending" | "running" | "done" | "failed" | "cancelled"; report: string | null; createdAt: string; finishedAt: string | null }

const helperLine = (h?: Helper) =>
  !h?.configured ? "The helper on your Mac isn't set up yet." : h.connected ? "Your Mac is connected." : `Your Mac isn't connected right now${h.seenAt ? ` (last seen ${fmtDate(h.seenAt)})` : ""} — it will pick this up when it is.`;

export function HandoffBox({ itemId }: { itemId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["handoffs", itemId], queryFn: () => api<{ enabled: boolean; handoffs: Handoff[]; helper?: Helper }>(`/api/work/items/${itemId}/handoffs`), refetchInterval: (x) => (x.state.data?.handoffs.some((h) => h.status === "pending" || h.status === "running") ? 10_000 : false) });
  const sessions = useQuery({ queryKey: ["handoff-sessions"], queryFn: () => api<{ sessions: Session[]; helper: Helper }>("/api/handoffs/sessions"), enabled: !!q.data?.enabled });
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [note, setNote] = useState("");
  const refresh = () => void qc.invalidateQueries({ queryKey: ["handoffs", itemId] });
  const start = useMutation({ mutationFn: () => post(`/api/work/items/${itemId}/handoff`, { sessionId: sid, note }), onSuccess: () => { setOpen(false); setNote(""); refresh(); } });
  const act = useMutation({ mutationFn: (v: { id: string; action: "retry" | "cancel" }) => post(`/api/handoffs/${v.id}/${v.action}`, {}), onSuccess: refresh });
  useEffect(() => { if (!sid && sessions.data?.sessions[0]) setSid(sessions.data.sessions[0].id); }, [sessions.data, sid]);
  if (!q.data?.enabled) return null;
  const list = q.data.handoffs;
  const active = list.some((h) => h.status === "pending" || h.status === "running");
  return (
    <Card title="Claude Code" icon={<Bot size={16} />} actions={!active && !open ? <button className="btn btn-sm" onClick={() => setOpen(true)}>Hand to Claude Code</button> : undefined}>
      <div className="space-y-3 text-sm">
        {open && (
          sessions.data?.sessions.length ? (
            <div className="space-y-2">
              <select className="input" value={sid} onChange={(e) => setSid(e.target.value)} aria-label="Claude Code session">
                {sessions.data.sessions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <textarea className="input min-h-16" placeholder="What should it do? (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <ErrorNote error={start.error} />
              <div className="flex gap-2">
                <button className="btn btn-sm btn-primary" disabled={!sid || start.isPending} onClick={() => start.mutate()}>Hand over</button>
                <button className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button>
              </div>
              <p className="muted text-xs">{helperLine(sessions.data.helper)}</p>
            </div>
          ) : <p className="muted">No Claude Code sessions yet — add them in <a className="text-brand-700 hover:underline" href="/assistant/settings">Fusion AI settings</a>.</p>
        )}
        {list.length === 0 && !open && <p className="muted">Hand this task to one of your Claude Code projects; the report comes back here.</p>}
        {list.map((h) => (
          <div key={h.id} className="rounded-lg border border-[var(--line)] px-3 py-2">
            <div className="flex items-center gap-2">
              {h.status === "done" ? <CheckCircle2 size={14} className="text-emerald-600" /> : h.status === "failed" ? <CircleAlert size={14} className="text-red-600" /> : h.status === "cancelled" ? <X size={14} className="muted" /> : <Loader2 size={14} className="animate-spin" />}
              <b>{h.name}</b>
              <span className="muted text-xs">{h.status === "pending" ? "waiting for your Mac" : h.status === "running" ? "working" : h.status} · {fmtDate(h.finishedAt ?? h.createdAt)}</span>
              <span className="ml-auto flex gap-1">
                {h.status === "pending" && <button className="btn btn-sm" title="Cancel" onClick={() => act.mutate({ id: h.id, action: "cancel" })}><X size={13} /> Cancel</button>}
                {(h.status === "failed" || h.status === "cancelled") && !active && <button className="btn btn-sm" onClick={() => act.mutate({ id: h.id, action: "retry" })}><RotateCcw size={13} /> Retry</button>}
              </span>
            </div>
            {h.note && <p className="muted mt-1 text-xs">Note: {h.note}</p>}
            {h.report && <p className="mt-2 whitespace-pre-wrap">{h.report}</p>}
          </div>
        ))}
        <ErrorNote error={act.error} />
      </div>
    </Card>
  );
}

/** The session list (name + project folder on your Mac), for Fusion AI settings. */
export function HandoffSessions() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["handoff-sessions"], queryFn: () => api<{ sessions: Session[]; helper: Helper }>("/api/handoffs/sessions"), retry: false });
  const [rows, setRows] = useState<Array<Partial<Session>>>([]);
  useEffect(() => { if (q.data) setRows(q.data.sessions); }, [q.data]);
  const save = useMutation({
    mutationFn: () => put<{ sessions: Session[]; helper: Helper }>("/api/handoffs/sessions", { sessions: rows.filter((r) => r.name?.trim() || r.path?.trim()) }),
    onSuccess: (d) => { qc.setQueryData(["handoff-sessions"], d); },
  });
  if (q.isError || !q.data) return null; // not set up for this person
  const set = (i: number, k: "name" | "path", v: string) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <Card title="Claude Code sessions" icon={<Bot size={16} />}>
      <div className="space-y-2 text-sm">
        <p className="muted">Projects on your Mac you can hand tasks to. The folder must be inside ~/Documents/Claude-Projects (your Mac refuses anything else).</p>
        {rows.map((r, i) => (
          <div key={r.id ?? `new-${i}`} className="flex flex-wrap items-center gap-2">
            <input className="input !w-40" placeholder="Name" value={r.name ?? ""} onChange={(e) => set(i, "name", e.target.value)} aria-label="Session name" />
            <input className="input min-w-0 flex-1" placeholder="/Users/…/Documents/Claude-Projects/Project" value={r.path ?? ""} onChange={(e) => set(i, "path", e.target.value)} aria-label="Project folder" />
            <button className="btn btn-sm" aria-label="Remove" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}><Trash2 size={13} /></button>
          </div>
        ))}
        <div className="flex gap-2">
          <button className="btn btn-sm" onClick={() => setRows((r) => [...r, { name: "", path: "" }])}><Plus size={13} /> Add</button>
          <button className="btn btn-sm btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
        </div>
        <ErrorNote error={save.error} />
        <p className="muted text-xs">{helperLine(q.data.helper)}</p>
      </div>
    </Card>
  );
}
